import axios from 'axios';
import type { UserTier, TierLimits } from './tierLimits';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

export interface CaseStudy {
  _id: string;
  projectName: string;
  country: string;
  description: string;
  photos: {
    url: string;
    filename: string;
    uploadDate?: string;
  }[];
  location: {
    type: string;
    coordinates: [number, number];
  };
  hideFromMap?: boolean;
  approximateLocation?: boolean;
  caseStudyUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Lean shape returned by /map-markers — only the fields MapView/EmbeddedMapView actually render.
export interface MapMarker {
  _id: string;
  projectName: string;
  country: string;
  description: string;
  photos: { url: string }[];
  location: {
    type: string;
    coordinates: [number, number];
  };
  approximateLocation?: boolean;
  hideFromMap?: boolean;
  caseStudyUrl?: string | null;
}

// Lean shape returned by /dashboard-records — only the fields the admin table actually renders.
export interface DashboardRecord {
  _id: string;
  projectName: string;
  country: string;
  createdAt: string;
  photoCount: number;
  hideFromMap?: boolean;
  approximateLocation?: boolean;
}

// Tier types and the fallback limits map live in ./tierLimits (axios-free so the drift test can
// import them); re-exported here because the app imports them from the api module.
export type { UserTier, TierLimits } from './tierLimits';
export { TIER_LIMITS } from './tierLimits';

export interface Company {
  _id?: string;
  name: string;
  logo?: {
    url: string;
    filename: string;
  };
  hideLogo?: boolean;
  primaryColor: string;
  markerColor?: string;
  ownerTier?: UserTier;
  createdAt?: string;
  updatedAt?: string;
}

// Shapes returned by GET /api/admin/overview (platform-wide, admin only).
export interface PlatformUser {
  _id: string;
  username: string;
  tier: UserTier;
  isAdmin: boolean;
  createdAt: string;
  /** Per-tenant records plus that user's rows in the legacy top-level collection. */
  recordCount: number;
}

export interface PlatformOverview {
  users: PlatformUser[];
  totals: {
    userCount: number;
    proCount: number;
    freeCount: number;
    recordCount: number;
  };
}

export interface UserInfo {
  userId: string;
  username: string;
  tier: UserTier;
  /** Server-enforced limits for this user's tier. Optional so an older API still parses. */
  limits?: TierLimits;
  isAdmin: boolean;
  createdAt: string;
  isImpersonating?: boolean;
  adminId?: string | null;
  /** OAuth identity attached to this account, or null if none. At most one can be connected. */
  linkedProvider?: 'google' | 'microsoft' | null;
  /** Whether the account can also sign in with a password. */
  hasPassword?: boolean;
}

class ApiService {
  // User-scoped methods
  async getCaseStudies(userId?: string): Promise<CaseStudy[]> {
    const url = userId
      ? `${API_BASE_URL}/case-studies/${userId}`
      : `${API_BASE_URL}/case-studies`;
    const response = await axios.get(url);
    return response.data;
  }

  async getDashboardRecords(userId?: string): Promise<DashboardRecord[]> {
    const url = userId
      ? `${API_BASE_URL}/case-studies/${userId}/dashboard-records`
      : `${API_BASE_URL}/case-studies/dashboard-records`;
    const response = await axios.get(url);
    return response.data;
  }

  async getMapMarkers(userId?: string): Promise<MapMarker[]> {
    if (!userId) {
      // Legacy fallback — returns full CaseStudy objects, which are structurally a superset of MapMarker.
      return (await this.getCaseStudies()) as unknown as MapMarker[];
    }
    const response = await axios.get(`${API_BASE_URL}/case-studies/${userId}/map-markers`);
    return response.data;
  }

  async getCaseStudy(id: string, userId?: string): Promise<CaseStudy> {
    const url = userId
      ? `${API_BASE_URL}/case-studies/${userId}/case-study/${id}`
      : `${API_BASE_URL}/case-studies/${id}`;
    const response = await axios.get(url);
    return response.data;
  }

  async createCaseStudy(formData: FormData, userId?: string): Promise<CaseStudy> {
    const url = userId
      ? `${API_BASE_URL}/case-studies/${userId}`
      : `${API_BASE_URL}/case-studies`;
    const response = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateCaseStudy(id: string, formData: FormData, userId?: string): Promise<CaseStudy> {
    const url = userId
      ? `${API_BASE_URL}/case-studies/${userId}/case-study/${id}`
      : `${API_BASE_URL}/case-studies/${id}`;
    const response = await axios.put(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteCaseStudy(id: string, userId?: string): Promise<void> {
    const url = userId
      ? `${API_BASE_URL}/case-studies/${userId}/case-study/${id}`
      : `${API_BASE_URL}/case-studies/${id}`;
    await axios.delete(url);
  }

  async getLastLocation(userId?: string): Promise<{ lat: number; lng: number } | null> {
    const url = userId
      ? `${API_BASE_URL}/case-studies/${userId}/last-location`
      : `${API_BASE_URL}/case-studies/last-location`;
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (err: any) {
      if (err.response?.status === 404) return null;
      throw err;
    }
  }

  async getNearbyCaseStudies(lat: number, lng: number, maxDistance?: number, userId?: string): Promise<CaseStudy[]> {
    const url = userId
      ? `${API_BASE_URL}/case-studies/${userId}/nearby/${lat}/${lng}`
      : `${API_BASE_URL}/case-studies/nearby/${lat}/${lng}`;
    const response = await axios.get(url, { params: { maxDistance } });
    return response.data;
  }

  // Specific method for getting user-specific case studies for embed
  async getUserCaseStudies(userId: string): Promise<CaseStudy[]> {
    const response = await axios.get(`${API_BASE_URL}/case-studies/${userId}`);
    return response.data;
  }

  // Company configuration methods
  async getCompany(userId?: string): Promise<Company> {
    const url = userId
      ? `${API_BASE_URL}/company/${userId}`
      : `${API_BASE_URL}/company`;
    const response = await axios.get(url);
    return response.data;
  }

  async updateCompany(formData: FormData, userId?: string): Promise<Company> {
    const url = userId
      ? `${API_BASE_URL}/company/${userId}`
      : `${API_BASE_URL}/company`;
    const response = await axios.put(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteCompanyLogo(userId?: string): Promise<Company> {
    const url = userId
      ? `${API_BASE_URL}/company/${userId}/logo`
      : `${API_BASE_URL}/company/logo`;
    const response = await axios.delete(url);
    return response.data;
  }

  // User info method (requires auth)
  async getUserInfo(): Promise<UserInfo> {
    const response = await axios.get(`${API_BASE_URL}/auth/me`);
    return response.data;
  }

  // Mint a short-lived token authorising "attach an OAuth identity to my account". It is handed
  // to GET /auth/{provider}?link=<token> as the OAuth `state`, since a redirect can't carry the
  // Authorization header. 403s while impersonating.
  async startOAuthLink(): Promise<{ token: string }> {
    const response = await axios.post(`${API_BASE_URL}/auth/link/start`);
    return response.data;
  }

  // Get all users (admin only) - for impersonation
  async getUsers(): Promise<Array<{ _id: string; username: string; tier: UserTier; createdAt: string; isAdmin: boolean }>> {
    const response = await axios.get(`${API_BASE_URL}/auth/users`);
    return response.data;
  }

  // Platform-wide stats (admin only) - backs the /admin/platform page
  async getPlatformOverview(): Promise<PlatformOverview> {
    const response = await axios.get(`${API_BASE_URL}/admin/overview`);
    return response.data;
  }
}

export default new ApiService();